import { formatDistanceToNow, parseISO } from 'date-fns';
import { ArrowLeft, BarChart3, ChevronDown, Loader2, MapPin, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { BaseLayout } from '@/shared/components/layout';
import { getErrorMessage } from '@/shared/errors/app-error';
import { ErrorState } from '@/shared/errors/ErrorState';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useUser } from '@/shared/hooks/useUser';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

import { safetyPinKey } from './geometry';
import { useMoveSafetyPin, useSafetyIssues, useSafetyLayouts } from './hooks';
import { SafetyIssueSummary } from './SafetyIssueSummary';
import { SafetyIssueForm } from './SafetyIssueForm';
import { SafetyLayoutCanvas } from './SafetyLayoutCanvas';
import { SafetyOverview } from './SafetyOverview';
import { SafetyPinDetailsDialog } from './SafetyPinDetailsDialog';
import { SafetyViewTabs } from './SafetyViewTabs';
import type { SafetyIssue, SafetyIssueStatus, SafetyPinDraft } from './types';
import { useSafetySummaryVisibility } from './useSafetySummaryVisibility';

type StatusFilter = 'ALL' | SafetyIssueStatus;

const linkedPinFrom = (searchParams: URLSearchParams): SafetyPinDraft | null => {
  const layoutId = Number(searchParams.get('layout'));
  const x = Number(searchParams.get('x'));
  const y = Number(searchParams.get('y'));
  if (
    !Number.isInteger(layoutId) ||
    layoutId <= 0 ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    x < 0 ||
    x > 1 ||
    y < 0 ||
    y > 1
  ) {
    return null;
  }
  return { layoutId, x, y };
};

export const SafetyPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const linkedPin = linkedPinFrom(searchParams);
  const { projectIdNumber: projectId } = useCurrentProject();
  const { user } = useUser();
  const [selectedLayoutId, setSelectedLayoutId] = useState<number | undefined>(linkedPin?.layoutId);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [pinDraft, setPinDraft] = useState<SafetyPinDraft | null>(null);
  const [selectedPin, setSelectedPin] = useState<SafetyPinDraft | null>(linkedPin);
  const { isSummaryVisible, toggleSummary } = useSafetySummaryVisibility();
  const layouts = useSafetyLayouts(projectId);
  const issues = useSafetyIssues(projectId);
  const movePin = useMoveSafetyPin(projectId);
  const currentUserId = user ? Number(user.id) : null;
  const issuesPath = `${location.pathname.replace(/\/+$/, '')}/issues`;

  const selectedLayout = layouts.data?.find((layout) => layout.id === selectedLayoutId);
  const allIssues = issues.data ?? [];
  const openIssueCount = allIssues.filter((issue) => issue.status === 'OPEN').length;
  const resolvedIssueCount = allIssues.filter((issue) => issue.status === 'RESOLVED').length;
  const layoutIssues = useMemo(
    () => allIssues.filter((issue) => issue.layout === selectedLayoutId),
    [allIssues, selectedLayoutId]
  );
  const visibleIssues = useMemo(
    () => layoutIssues.filter((issue) => statusFilter === 'ALL' || issue.status === statusFilter),
    [layoutIssues, statusFilter]
  );
  const selectedPinIssues = useMemo(() => {
    if (!selectedPin) return [];
    const selectedKey = safetyPinKey(selectedPin.x, selectedPin.y);
    return allIssues.filter(
      (issue) =>
        issue.layout === selectedPin.layoutId &&
        issue.position_x != null &&
        issue.position_y != null &&
        safetyPinKey(issue.position_x, issue.position_y) === selectedKey
    );
  }, [allIssues, selectedPin]);
  const selectedPinLayoutName = selectedPin
    ? (layouts.data?.find((layout) => layout.id === selectedPin.layoutId)?.name ?? 'Selected area')
    : '';

  if (!projectId) {
    return (
      <BaseLayout>
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <ErrorState title="Select a project" message="Safety issues are scoped to a project." />
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto w-full max-w-[1800px] px-4 py-6 sm:px-6 lg:px-9 lg:py-8">
        <div className="mb-[22px] min-w-0">
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em] md:text-[2.4rem]">
            Safety
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track and resolve project safety issues.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
            aria-expanded={isSummaryVisible}
            aria-controls="safety-summary"
            onClick={toggleSummary}
          >
            <BarChart3 className="size-4" />
            {isSummaryVisible ? 'Hide summary' : 'Show summary'}
            <ChevronDown
              className={`size-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>

        {isSummaryVisible && !issues.isLoading && !issues.isError && (
          <div id="safety-summary" className="mb-4">
            <SafetyIssueSummary
              openCount={openIssueCount}
              openIssuesPath={`${issuesPath}?status=OPEN`}
              resolvedCount={resolvedIssueCount}
              issues={allIssues}
            />
          </div>
        )}

        <div className="mb-6">
          <SafetyViewTabs active="map" mapPath={location.pathname} listPath={issuesPath} />
        </div>

        {layouts.isError || issues.isError ? (
          <ErrorState
            title="Safety could not be loaded"
            error={layouts.error ?? issues.error}
            onRetry={() => {
              void layouts.refetch();
              void issues.refetch();
            }}
          />
        ) : layouts.isLoading || issues.isLoading ? (
          <Card className="grid min-h-[520px] place-items-center">
            <Loader2 className="size-7 animate-spin text-primary" />
          </Card>
        ) : !selectedLayout ? (
          <SafetyOverview
            layouts={layouts.data ?? []}
            issues={allIssues}
            onSelectLayout={(layoutId) => {
              setSelectedLayoutId(layoutId);
              setSelectedPin(null);
              setStatusFilter('ALL');
            }}
            onSelectIssue={(issue: SafetyIssue) => {
              if (issue.layout == null || issue.position_x == null || issue.position_y == null) {
                return;
              }
              setSelectedPin({
                layoutId: issue.layout,
                x: issue.position_x,
                y: issue.position_y,
                locationId: issue.location ?? undefined,
                locationName: issue.location_detail?.name,
              });
              setStatusFilter('ALL');
            }}
          />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                className="-ml-3 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSelectedLayoutId(undefined);
                  setSelectedPin(null);
                }}
              >
                <ArrowLeft className="mr-1 size-4" />
                Safety overview
              </Button>
              <div className="grid flex-1 gap-3 sm:grid-cols-2 lg:max-w-[652px] lg:grid-cols-2">
                <Select
                  value={String(selectedLayoutId)}
                  onValueChange={(value) => {
                    setSelectedLayoutId(Number(value));
                    setSelectedPin(null);
                    setStatusFilter('ALL');
                  }}
                >
                  <SelectTrigger aria-label="Select safety layout">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(layouts.data ?? []).map((layout) => (
                      <SelectItem key={layout.id} value={String(layout.id)}>
                        {layout.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as StatusFilter)}
                >
                  <SelectTrigger aria-label="Filter safety issues by status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All issues</SelectItem>
                    <SelectItem value="OPEN">Open issues</SelectItem>
                    <SelectItem value="RESOLVED">Resolved issues</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {movePin.error && (
              <p role="alert" className="mb-4 text-sm text-destructive">
                {getErrorMessage(movePin.error)}
              </p>
            )}

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="h-[min(72vh,780px)] min-h-[520px]">
                <SafetyLayoutCanvas
                  layout={selectedLayout}
                  issues={visibleIssues}
                  issuesForOwnership={layoutIssues}
                  currentUserId={currentUserId}
                  selectedIssueId={
                    selectedPinIssues.find(
                      (issue) => statusFilter === 'ALL' || issue.status === statusFilter
                    )?.id
                  }
                  onDropPin={(pin) => {
                    setPinDraft(pin);
                    setSelectedPin(null);
                  }}
                  onMoveIssues={({ issueIds, x, y }) => {
                    movePin.reset();
                    setSelectedPin(null);
                    return movePin
                      .mutateAsync({
                        project_id: projectId,
                        issue_ids: issueIds,
                        position_x: x,
                        position_y: y,
                      })
                      .then(() => undefined);
                  }}
                  onSelectIssues={(pinIssues) => {
                    const issue = pinIssues[0];
                    if (issue.position_x == null || issue.position_y == null) return;
                    setSelectedPin({
                      layoutId: issue.layout ?? selectedLayout.id,
                      x: issue.position_x,
                      y: issue.position_y,
                      locationId: issue.location ?? undefined,
                      locationName: issue.location_detail?.name,
                    });
                  }}
                />
              </div>

              <Card className="min-h-0 overflow-hidden">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="flex items-center justify-between gap-3 text-base">
                    <span>{selectedLayout.name} issues</span>
                    <Badge variant="outline">{visibleIssues.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[min(68vh,720px)] space-y-2 overflow-y-auto p-3">
                  {visibleIssues.length === 0 ? (
                    <div className="px-3 py-12 text-center text-sm text-muted-foreground">
                      No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} safety issues on
                      this layout.
                    </div>
                  ) : (
                    visibleIssues.map((issue) => {
                      const active = Boolean(
                        selectedPin &&
                          issue.position_x != null &&
                          issue.position_y != null &&
                          safetyPinKey(issue.position_x, issue.position_y) ===
                            safetyPinKey(selectedPin.x, selectedPin.y)
                      );
                      return (
                        <button
                          type="button"
                          key={issue.id}
                          onClick={() => {
                            if (issue.position_x == null || issue.position_y == null) return;
                            setSelectedPin({
                              layoutId: issue.layout ?? selectedLayout.id,
                              x: issue.position_x,
                              y: issue.position_y,
                              locationId: issue.location ?? undefined,
                              locationName: issue.location_detail?.name,
                            });
                          }}
                          className={`w-full rounded-[13px] border p-3 text-left transition-colors ${
                            active
                              ? 'border-primary bg-primary/10 ring-2 ring-primary/15'
                              : 'border-border bg-muted/40 hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          <span className="flex items-start justify-between gap-3">
                            <span className="line-clamp-3 text-sm font-semibold leading-5">
                              {issue.description}
                            </span>
                            <Badge variant={issue.status === 'OPEN' ? 'destructive' : 'secondary'}>
                              {issue.status === 'OPEN' ? 'Open' : 'Resolved'}
                            </Badge>
                          </span>
                          <span className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            {issue.location_detail && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="size-3" />
                                {issue.location_detail.name}
                              </span>
                            )}
                            {issue.trade_detail && (
                              <span className="inline-flex items-center gap-1">
                                <Wrench className="size-3" />
                                {issue.trade_detail.name}
                              </span>
                            )}
                            <span>
                              {formatDistanceToNow(parseISO(issue.created_at), { addSuffix: true })}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>

      <Dialog open={Boolean(pinDraft)} onOpenChange={(open) => !open && setPinDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New safety issue</DialogTitle>
            <DialogDescription>
              The pin is placed on {selectedLayout?.name}. Add the issue details below.
            </DialogDescription>
          </DialogHeader>
          {pinDraft && (
            <SafetyIssueForm
              projectId={projectId}
              pin={pinDraft}
              onSuccess={() => setPinDraft(null)}
              onCancel={() => setPinDraft(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {selectedPin && selectedPinIssues.length > 0 && (
        <SafetyPinDetailsDialog
          key={`${selectedPin.layoutId}:${selectedPin.x}:${selectedPin.y}`}
          projectId={projectId}
          layoutName={selectedPinLayoutName}
          pin={selectedPin}
          issues={selectedPinIssues}
          onClose={() => setSelectedPin(null)}
        />
      )}
    </BaseLayout>
  );
};
